/* eslint-disable */
/**
 * Forked from react-numeric-input v2.2.3 (https://github.com/vlad-ignatov/react-numeric-input)
 * Updated for React 19 compatibility:
 */
import React, { Component } from "react"
import PropTypes from "prop-types"

const KEYCODE_UP = 38
const KEYCODE_DOWN = 40
const RE_NUMBER = /^[+-]?((\.\d+)|(\d+(\.\d+)?))$/
const RE_INCOMPLETE_NUMBER = /^([+-]|\.0*|[+-]\.0*|[+-]?\d+\.)?$/

function access(object, prop, defaultValue, ...args) {
  let result = object[prop]
  if (typeof result === "function") {
    result = result(...args)
  }
  return result === undefined ? defaultValue : result
}

class NumericInput extends Component {
  static propTypes = {
    step: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
    min: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
    max: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
    precision: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
    maxLength: PropTypes.number,
    parse: PropTypes.func,
    format: PropTypes.func,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    required: PropTypes.bool,
    snap: PropTypes.bool,
    noValidate: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    type: PropTypes.string,
    pattern: PropTypes.string,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onKeyDown: PropTypes.func,
    onChange: PropTypes.func,
    onInvalid: PropTypes.func,
    onValid: PropTypes.func,
    onInput: PropTypes.func,
    onSelect: PropTypes.func,
    size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    strict: PropTypes.bool,
    componentClass: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  }

  static defaultProps = {
    step: 1,
    min: Number.MIN_SAFE_INTEGER || -9007199254740991,
    max: Number.MAX_SAFE_INTEGER || 9007199254740991,
    precision: null,
    parse: null,
    format: null,
    strict: false,
    componentClass: "input",
    style: {},
  }

  static SPEED = 50
  static DELAY = 500
  static DIRECTION_UP = "up"
  static DIRECTION_DOWN = "down"

  constructor(...args) {
    super(...args)

    this._isStrict = !!this.props.strict

    this.state = {
      stringValue: "",
      ...this._propsToState(this.props),
    }
    this.stop = this.stop.bind(this)
    this.refsInput = {}
  }

  _propsToState(props) {
    const out = {}

    if (props.hasOwnProperty("value")) {
      out.stringValue = String(
        props.value || props.value === 0 ? props.value : ""
      ).trim()

      out.value = out.stringValue !== "" ? this._parse(props.value) : null
    } else if (!this._isMounted && props.hasOwnProperty("defaultValue")) {
      out.stringValue = String(
        props.defaultValue || props.defaultValue === 0
          ? props.defaultValue
          : ""
      ).trim()

      out.value =
        props.defaultValue !== "" ? this._parse(props.defaultValue) : null
    }

    return out
  }

  /**
   * Replaced componentWillReceiveProps for React 19 compatibility.
   */
  componentDidUpdate(prevProps, prevState, snapshot) {
    // Handle prop changes (replaces componentWillReceiveProps)
    if (prevProps.value !== this.props.value
      || prevProps.strict !== this.props.strict
      || prevProps.defaultValue !== this.props.defaultValue) {
      this._isStrict = !!this.props.strict
      const nextState = this._propsToState(this.props)
      if (Object.keys(nextState).length) {
        this._ignoreValueChange = true
        this.setState(nextState, () => {
          this._ignoreValueChange = false
        })
      }
    }

    // Restore selection from snapshot (replaces componentWillUpdate + old componentDidUpdate selection logic)
    if (snapshot != null) {
      if (snapshot.selectionStart != null) {
        this.refsInput.selectionStart = snapshot.selectionStart
      }
      if (snapshot.selectionEnd != null) {
        this.refsInput.selectionEnd = snapshot.selectionEnd
      }
    }

    // Call the onChange if needed
    if (
      !this._ignoreValueChange &&
      prevState.value !== this.state.value &&
      (!isNaN(this.state.value) || this.state.value === null)
    ) {
      this._invokeEventCallback(
        "onChange",
        this.state.value,
        this.refsInput.value,
        this.refsInput
      )
    }

    // Focus the input if needed
    if (this._inputFocus) {
      this.refsInput.focus()

      if (this.state.selectionStart || this.state.selectionStart === 0) {
        this.refsInput.selectionStart = this.state.selectionStart
      }

      if (this.state.selectionEnd || this.state.selectionEnd === 0) {
        this.refsInput.selectionEnd = this.state.selectionEnd
      }
    }

    this.checkValidity()
  }

  /**
   * Replaced componentWillUpdate for React 19 compatibility.
   * Saves input selection before render so it can be restored after.
   */
  getSnapshotBeforeUpdate() {
    if (this.refsInput && this.refsInput.selectionStart != null) {
      return {
        selectionStart: this.refsInput.selectionStart,
        selectionEnd: this.refsInput.selectionEnd,
      }
    }
    return null
  }

  componentWillUnmount() {
    this._isMounted = false
    this.stop()
  }

  componentDidMount() {
    this._isMounted = true
    this.refsInput.getValueAsNumber = () => this.state.value || 0

    this.refsInput.setValue = (value) => {
      this.setState({
        value: this._parse(value),
        stringValue: value,
      })
    }

    if (
      !this._inputFocus &&
      typeof document !== "undefined" &&
      document.activeElement === this.refsInput
    ) {
      this._inputFocus = true
      this.refsInput.focus()
      this._invokeEventCallback("onFocus", {
        target: this.refsInput,
        type: "focus",
      })
    }

    this.checkValidity()
  }

  saveSelection() {
    this.state.selectionStart = this.refsInput.selectionStart
    this.state.selectionEnd = this.refsInput.selectionEnd
  }

  checkValidity() {
    let validationError = ""

    const supportsValidation = !!this.refsInput.checkValidity

    const noValidate = !!(
      this.props.noValidate && this.props.noValidate !== "false"
    )

    this.refsInput.noValidate = noValidate

    let valid = noValidate || !supportsValidation

    if (valid) {
      validationError = ""
    } else {
      if (this.refsInput.pattern === "") {
        this.refsInput.pattern = this.props.required ? ".+" : ".*"
      }

      if (supportsValidation) {
        this.refsInput.checkValidity()
        valid = this.refsInput.validity.valid

        if (!valid) {
          validationError = this.refsInput.validationMessage
        }
      }

      if (valid && supportsValidation && this.props.maxLength) {
        if (this.refsInput.value.length > this.props.maxLength) {
          validationError = "This value is too long"
        }
      }
    }

    validationError =
      validationError ||
      (valid ? "" : this.refsInput.validationMessage || "Unknown Error")

    const validStateChanged = this._valid !== validationError
    this._valid = validationError
    if (validationError) {
      if (validStateChanged) {
        this._invokeEventCallback(
          "onInvalid",
          validationError,
          this.state.value,
          this.refsInput.value
        )
      }
    } else {
      if (validStateChanged) {
        this._invokeEventCallback(
          "onValid",
          this.state.value,
          this.refsInput.value
        )
      }
    }
  }

  _toNumber(x) {
    let n = parseFloat(x)
    if (isNaN(n) || !isFinite(n)) {
      n = 0
    }

    if (this._isStrict) {
      const precision = access(this.props, "precision", null, this)
      const q = Math.pow(10, precision === null ? 10 : precision)
      const _min = +access(
        this.props,
        "min",
        NumericInput.defaultProps.min,
        this
      )
      const _max = +access(
        this.props,
        "max",
        NumericInput.defaultProps.max,
        this
      )
      n = Math.min(Math.max(n, _min), _max)
      n = Math.round(n * q) / q
    }

    return n
  }

  _parse(x) {
    x = String(x)
    if (typeof this.props.parse === "function") {
      return parseFloat(this.props.parse(x))
    }
    return parseFloat(x)
  }

  _format(n) {
    let _n = this._toNumber(n)
    const precision = access(this.props, "precision", null, this)
    if (precision !== null) {
      _n = n.toFixed(precision)
    }

    _n += ""

    if (this.props.format) {
      return this.props.format(_n)
    }

    return _n
  }

  _step(n, callback) {
    const _isStrict = this._isStrict
    this._isStrict = true

    const _step = +access(
      this.props,
      "step",
      NumericInput.defaultProps.step,
      this,
      n > 0 ? NumericInput.DIRECTION_UP : NumericInput.DIRECTION_DOWN
    )

    let _n = this._toNumber((this.state.value || 0) + _step * n)

    if (this.props.snap) {
      _n = Math.round(_n / _step) * _step
    }

    this._isStrict = _isStrict

    if (_n !== this.state.value) {
      this.setState({ value: _n, stringValue: _n + "" }, callback)
      return true
    }

    return false
  }

  _onKeyDown(...args) {
    args[0].persist()
    this._invokeEventCallback("onKeyDown", ...args)
    const e = args[0]
    if (!e.isDefaultPrevented()) {
      if (e.keyCode === KEYCODE_UP) {
        e.preventDefault()
        this._step(
          e.ctrlKey || e.metaKey ? 0.1 : e.shiftKey ? 10 : 1
        )
      } else if (e.keyCode === KEYCODE_DOWN) {
        e.preventDefault()
        this._step(
          e.ctrlKey || e.metaKey ? -0.1 : e.shiftKey ? -10 : -1
        )
      } else {
        const value = this.refsInput.value
        const length = value.length
        if (e.keyCode === 8) {
          // backspace
          if (
            this.refsInput.selectionStart === this.refsInput.selectionEnd &&
            this.refsInput.selectionEnd > 0 &&
            value.length &&
            value.charAt(this.refsInput.selectionEnd - 1) === "."
          ) {
            e.preventDefault()
            this.refsInput.selectionStart = this.refsInput.selectionEnd =
              this.refsInput.selectionEnd - 1
          }
        } else if (e.keyCode === 46) {
          // delete
          if (
            this.refsInput.selectionStart === this.refsInput.selectionEnd &&
            this.refsInput.selectionEnd < length + 1 &&
            value.length &&
            value.charAt(this.refsInput.selectionEnd) === "."
          ) {
            e.preventDefault()
            this.refsInput.selectionStart = this.refsInput.selectionEnd =
              this.refsInput.selectionEnd + 1
          }
        }
      }
    }
  }

  stop() {
    if (this._timer) {
      clearTimeout(this._timer)
    }
  }

  increase(_recursive = false, callback) {
    this.stop()
    this._step(1, callback)
    const _max = +access(
      this.props,
      "max",
      NumericInput.defaultProps.max,
      this
    )
    if (isNaN(this.state.value) || +this.state.value < _max) {
      this._timer = setTimeout(() => {
        this.increase(true)
      }, _recursive ? NumericInput.SPEED : NumericInput.DELAY)
    }
  }

  decrease(_recursive = false, callback) {
    this.stop()
    this._step(-1, callback)
    const _min = +access(
      this.props,
      "min",
      NumericInput.defaultProps.min,
      this
    )
    if (isNaN(this.state.value) || +this.state.value > _min) {
      this._timer = setTimeout(() => {
        this.decrease(true)
      }, _recursive ? NumericInput.SPEED : NumericInput.DELAY)
    }
  }

  _invokeEventCallback(callbackName, ...args) {
    if (typeof this.props[callbackName] === "function") {
      this.props[callbackName].call(null, ...args)
    }
  }

  render() {
    const props = this.props
    const state = this.state

    const {
      // These are ignored in rendering
      step,
      min,
      max,
      precision,
      parse,
      format,
      snap,
      componentClass,
      value,
      type,
      style,
      defaultValue,
      onInvalid,
      onValid,
      strict,
      noStyle,

      // The rest are passed to the input
      ...rest
    } = this.props

    let stringValue = String(
      state.stringValue ||
        (state.value || state.value === 0 ? state.value : "") ||
        ""
    )

    const loose = !this._isStrict && (this._inputFocus || !this._isMounted)

    const attrs = {
      ref: (e) => {
        if (e != null) {
          this.refsInput = e
        }
      },
      type: "text",
      ...rest,
    }

    // incomplete number
    if (loose && RE_INCOMPLETE_NUMBER.test(stringValue)) {
      attrs.value = stringValue
    }
    // Not a number and not empty (loose mode only)
    else if (loose && stringValue && !RE_NUMBER.test(stringValue)) {
      attrs.value = stringValue
    }
    // number
    else if (state.value || state.value === 0) {
      attrs.value = this._format(state.value)
    }
    // empty
    else {
      attrs.value = ""
    }

    // Attach event listeners if the widget is not disabled
    if (!props.disabled && !props.readOnly) {
      Object.assign(attrs, {
        onChange: (e) => {
          const original = e.target.value
          let val = this._parse(original)
          if (isNaN(val)) {
            val = null
          }
          this.setState({
            value: this._isStrict ? this._toNumber(val) : val,
            stringValue: original,
          })
        },
        onKeyDown: this._onKeyDown.bind(this),
        onInput: (...args) => {
          this.saveSelection()
          this._invokeEventCallback("onInput", ...args)
        },
        onSelect: (...args) => {
          this.saveSelection()
          this._invokeEventCallback("onSelect", ...args)
        },
        onFocus: (...args) => {
          args[0].persist()
          this._inputFocus = true
          const val = this._parse(args[0].target.value)
          this.setState(
            {
              value: val,
              stringValue: val || val === 0 ? val + "" : "",
            },
            () => {
              this._invokeEventCallback("onFocus", ...args)
            }
          )
        },
        onBlur: (...args) => {
          const _isStrict = this._isStrict
          this._isStrict = true
          args[0].persist()
          this._inputFocus = false
          const val = this._parse(args[0].target.value)
          this.setState(
            {
              value: val,
            },
            () => {
              this._invokeEventCallback("onBlur", ...args)
              this._isStrict = _isStrict
            }
          )
        },
      })
    }

    const InputTag = componentClass || "input"

    return <InputTag {...attrs} />
  }
}

export default NumericInput
